import { useState, useRef, useEffect } from 'react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Check, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { searchCitiesWithCoords, type USCityWithCoords } from '@/data/usCitiesWithCoords';

export interface LocationValue {
  city: string;
  state: string;
  stateCode: string;
  displayName: string;
}

interface LocationAutocompleteProps {
  value?: LocationValue | null;
  onChange: (value: LocationValue | null) => void;
  placeholder?: string;
  className?: string;
}

const inputStyle = 'h-10 field-surface field-border data-[state=open]:border-white/40 transition-colors';

export function LocationAutocomplete({ 
  value, 
  onChange, 
  placeholder = "Search city...",
  className 
}: LocationAutocompleteProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ReturnType<typeof searchCitiesWithCoords>>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const cities = searchCitiesWithCoords(query, 8);
    setResults(cities);
  }, [query]);

  const handleSelect = (city: typeof results[0]) => {
    onChange({
      city: city.city,
      state: city.state,
      stateCode: city.stateCode,
      displayName: city.displayName
    });
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-start text-left font-normal",
            inputStyle,
            !value && "text-muted-foreground",
            className
          )}
        >
          
          {value ? value.displayName : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0 bg-popover dark:bg-card border-border" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            ref={inputRef}
            placeholder="Search cities..."
            value={query}
            onValueChange={setQuery}
            autoCapitalize="words"
          />
          <CommandList>
            <CommandEmpty>
              {query.length < 2 ? "Type at least 2 characters..." : "No cities found."}
            </CommandEmpty>
            {results.length > 0 && (
              <CommandGroup>
                {results.map((city) => (
                  <CommandItem
                    key={`${city.city}-${city.stateCode}`}
                    value={city.displayName}
                    onSelect={() => handleSelect(city)}
                    className="cursor-pointer text-foreground data-[selected=true]:text-foreground"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value?.displayName === city.displayName
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    <div className="flex flex-col">
                      <span>{city.displayName}</span>
                      <span className="text-xs text-foreground/60">{city.state}</span>
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
